import React from "react";
import { Checkbox, FormControlLabel, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import {
  VoicesTopicPayload,
  VoicesTopicSource,
} from "../../../Models/voicesTopics";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";
import VoicesSourcesEditor from "./VoicesSourcesEditor";

interface Props {
  value: VoicesTopicPayload;
  onChange: (value: VoicesTopicPayload) => void;
}

export const EMPTY_TOPIC: VoicesTopicPayload = {
  title: "",
  summary: "",
  sources: [{ url: "", name: "", date: null }],
  confidential: false,
  notes: "",
};

const sourceIsComplete = (source: VoicesTopicSource) =>
  /^https?:\/\/\S+/.test(source.url.trim()) && !!source.name.trim();

export const topicFormIsValid = (value: VoicesTopicPayload) =>
  !!value.title.trim() &&
  value.sources.length > 0 &&
  value.sources.every(sourceIsComplete);

const VoicesTopicForm: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation("voicesTopics");
  const { colors: workspace } = useWorkspaceColors();

  return (
    <Stack spacing={2.5}>
      <VoicesField label={t("title")}>
        <VoicesInput
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
        />
      </VoicesField>
      <VoicesField label={t("summary")} hint={t("summaryHint")}>
        <VoicesInput
          multiline
          minRows={3}
          value={value.summary}
          onChange={(e) => onChange({ ...value, summary: e.target.value })}
        />
      </VoicesField>
      <VoicesField label={t("sources")} hint={t("sourcesHint")}>
        <VoicesSourcesEditor
          sources={value.sources}
          onChange={(sources) => onChange({ ...value, sources })}
        />
      </VoicesField>
      <VoicesField label={t("notes")} hint={t("notesHint")}>
        <VoicesInput
          multiline
          minRows={2}
          value={value.notes}
          onChange={(e) => onChange({ ...value, notes: e.target.value })}
        />
      </VoicesField>
      <FormControlLabel
        control={
          <Checkbox
            checked={value.confidential}
            onChange={(e) => onChange({ ...value, confidential: e.target.checked })}
            sx={{ "&.Mui-checked": { color: workspace.accent } }}
          />
        }
        label={t("confidentialLabel")}
        sx={{ "& .MuiFormControlLabel-label": { color: color.gray900 } }}
      />
    </Stack>
  );
};

export default VoicesTopicForm;
